import axios from "axios";
import { useEffect } from "react";
import { toast } from "react-toastify";
// const BASE_URL = 'https://xpansedev.theuplife.in/'

// <-------- Development URL -------->
// const BASE_URL = 'https://xpanse.dev.bexcart.com/';
// const BASE_URL = 'https://venues-tone-occurrence-pub.trycloudflare.com/';

// <-------- Production URL -------->
// const BASE_URL = 'https://api.xpansecafe.com/';

// -----------> CURRENT PRODUCTION URL FOR ADMIN PANEL <-----------------
const BASE_URL = 'https://admin.api.xpansecafe.com/';

// -----------> OPS API BASE URL <-----------------
const OPS_BASE_URL = 'https://opsapis.xpansecoffee.com/';
const OPS_API_KEY = 'sYQ8SawD0mdqOtPO18NGs1paiEccyXJpFzsCiz1wXgC';
const OPS_API_SECRET = '79SvSQbGhcgCK9GdKTN61xTjiJvelIJVkC7pY7ioPaAOryyqbjy4pir2ckssjoCD';

const AUTH_ENDPOINTS = {
    refresh: 'auth/refreshtoken',
    signin: 'auth/admin/signin',
    logout: 'auth/admin/logout',
};

const isAuthEndpoint = (url = '', endpoint) => url.includes(endpoint);

// Create axios instances
const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 72000,
});

const opsAxiosInstance = axios.create({
    baseURL: OPS_BASE_URL,
    timeout: 72000,
});

const attachOpsAdminHeaders = (config = {}) => {
    const headers = config.headers || {};

    headers['X-API-Key'] = OPS_API_KEY;
    headers['X-API-Secret'] = OPS_API_SECRET;

    if (!headers['Content-Type'] && config.data && typeof config.data !== 'string') {
        headers['Content-Type'] = 'application/json';
    }

    config.headers = headers;
    return config;
};

// State management
const authState = {
    hasShownExpiredToast: false,
    isRedirecting: false,
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    failedQueue = [];
};

const AuthHelper = {
    clearAuthData() {
        const items = [
            "authToken",
            "refreshToken",
            "email",
            "userRole",
            "userId",
            "access",
            "user",
        ];
        items.forEach(item => localStorage.removeItem(item));
    },

    redirectToLogin() {
        if (authState.isRedirecting) return;

        authState.isRedirecting = true;

        if (!authState.hasShownExpiredToast) {
            toast.error("Session expired. Please login again.", {
                position: "top-right",
                autoClose: 2500,
            });
            authState.hasShownExpiredToast = true;
        }

        this.clearAuthData();

        setTimeout(() => {
            if (window.location.pathname !== '/') {
                window.location.href = '/';
            }
        }, 500);
    },

    validateToken(token) {
        if (!token || typeof token !== 'string' || token.trim() === '') {
            return false;
        }

        const parts = token.split('.');
        if (parts.length !== 3) {
            return false;
        }

        try {
            const payload = JSON.parse(atob(parts[1]));
            if (payload.exp && Date.now() >= payload.exp * 1000) {
                return false;
            }
            return true;
        } catch {
            return false;
        }
    },

    isTokenExpired(token) {
        return !this.validateToken(token);
    },

    setTokens(accessToken, refreshToken) {
        if (accessToken) {
            localStorage.setItem('authToken', accessToken);
        }
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }
    },
};

export const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
        throw new Error('No refresh token available');
    }


    const response = await axios.post(
        `${BASE_URL}${AUTH_ENDPOINTS.refresh}`,
        { refreshToken },
        {
            headers: { 'Content-Type': 'application/json' },
            timeout: 72000,
        }
    );

    console.log("Response of the Refresh Token Api :", response);


    if (response?.data?.status !== true) {
        throw new Error(response?.data?.data?.message || 'Failed to refresh session');
    }

    const newAccessToken = response?.data?.accessToken;
    const newRefreshToken = response?.data?.refreshToken || null;

    if (!newAccessToken) {
        throw new Error('No access token returned from refresh');
    }

    AuthHelper.setTokens(newAccessToken, newRefreshToken);

    return newAccessToken;
};

const enqueueFailedRequest = (instance, originalRequest) => {
    return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
    }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return instance(originalRequest);
    });
};

// Generic retry handler - works for any axios instance (main or ops)
const tryRefreshAndRetry = async (instance, originalRequest, error) => {
    const requestUrl = originalRequest?.url || '';

    if (
        originalRequest._retry ||
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.refresh) ||
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.signin) ||
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.logout)
    ) {
        AuthHelper.redirectToLogin();
        return Promise.reject(error);
    }

    if (isRefreshing) {
        return enqueueFailedRequest(instance, originalRequest);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
    } catch (refreshError) {
        processQueue(refreshError, null);
        AuthHelper.redirectToLogin();
        return Promise.reject(refreshError);
    } finally {
        isRefreshing = false;
    }
};

export const logoutUser = async () => {
    const token = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');

    try {
        await axiosInstance.post(
            AUTH_ENDPOINTS.logout,
            refreshToken ? { refreshToken } : {},
            token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        );
    } catch (error) {
        console.warn('Logout API failed, clearing local session anyway.', error);
    } finally {
        AuthHelper.clearAuthData();
    }
};

// Shared request interceptor logic - attaches/refreshes the auth token
const attachAuthToken = async (config) => {
    authState.hasShownExpiredToast = false;
    authState.isRedirecting = false;

    const requestUrl = config?.url || '';

    if (
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.refresh) ||
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.signin) ||
        isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.logout)
    ) {
        if (isAuthEndpoint(requestUrl, AUTH_ENDPOINTS.logout)) {
            const token = localStorage.getItem("authToken");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    }

    let token = localStorage.getItem("authToken");

    if (token && AuthHelper.isTokenExpired(token)) {
        const refreshToken = localStorage.getItem('refreshToken');

        if (refreshToken) {
            try {
                if (isRefreshing) {
                    token = await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });
                } else {
                    isRefreshing = true;
                    try {
                        token = await refreshAccessToken();
                        processQueue(null, token);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        throw refreshError;
                    } finally {
                        isRefreshing = false;
                    }
                }
            } catch {
                // Fall through; response interceptor will handle auth failure
            }
        }
    }

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
};

// Shared response error handling (non-auth-retry errors: toasts etc.)
const handleResponseError = (error) => {
    const { response, code, message } = error;

    if (!response) {
        if (code === "ECONNABORTED") {
            toast.error("Request timeout. Please try again.");
        } else if (message === "Network Error") {
            toast.error("Network error. Please check your connection.");
        }
        return;
    }

    const { status, data } = response;

    if (status === 400 || status === 422) {
        if (data?.message) {
            toast.error(data.message);
        }
        return;
    }

    if (status >= 500) {
        toast.error("Server error. Please try again later.");
    }
};

// ---------------- Main (BASE_URL) interceptors ----------------
axiosInstance.interceptors.request.use(
    attachAuthToken,
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    async (response) => {
        if (response.status === 515) {
            return tryRefreshAndRetry(axiosInstance, response.config, new Error("Authentication error"));
        }
        return response;
    },
    async (error) => {
        const { response, config: originalRequest } = error;

        if (!response) {
            handleResponseError(error);
            return Promise.reject(error);
        }

        if (response.status === 515) {
            return tryRefreshAndRetry(axiosInstance, originalRequest, error);
        }

        handleResponseError(error);
        return Promise.reject(error);
    }
);

// ---------------- Ops (OPS_BASE_URL) interceptors ----------------
opsAxiosInstance.interceptors.request.use(
    async (config) => {
        const configWithOpsHeaders = attachOpsAdminHeaders(config);
        return attachAuthToken(configWithOpsHeaders);
    },
    (error) => Promise.reject(error)
);

opsAxiosInstance.interceptors.response.use(
    async (response) => {
        if (response.status === 515) {
            return tryRefreshAndRetry(opsAxiosInstance, response.config, new Error("Authentication error"));
        }
        return response;
    },
    async (error) => {
        const { response, config: originalRequest } = error;

        if (!response) {
            handleResponseError(error);
            return Promise.reject(error);
        }

        if (response.status === 515) {
            return tryRefreshAndRetry(opsAxiosInstance, originalRequest, error);
        }

        handleResponseError(error);
        return Promise.reject(error);
    }
);

export { AuthHelper, opsAxiosInstance };
export default axiosInstance;